import { useMemo, useRef, useState, type KeyboardEvent } from "react";
import { useTranslation } from "react-i18next";

import {
  US_STATES,
  activeStates,
  type UsState,
  type UsStateCode,
} from "@/lib/states";

/**
 * apps/web :: components :: StatePicker
 *
 * Reusable leaf component for selecting one OR many US states from the
 * RoadBridge catalog. Used downstream by:
 *   - The onboarding wizard (`mode="single"`, the learner's home state).
 *   - The dashboard state switcher (`mode="multi"`, CDL drivers studying
 *     across state lines).
 *
 * Pure leaf — the parent owns the selection value and passes `onChange`.
 * No page wiring / routing / data-fetching here.
 *
 * Accessibility (WCAG 2.1 AA):
 *   - Container is `role="radiogroup"` in single mode, `role="group"` in
 *     multi mode. Both accept an `aria-label` override so callers can tag
 *     it with localised copy.
 *   - Each card is an `<button>` with `aria-checked` reflecting selection.
 *     (Technically radios/checkboxes, but cards are the idiomatic shape
 *     for this picker; `role` is promoted to `radio` / `checkbox` below.)
 *   - Keyboard: ArrowLeft/Right/Up/Down move focus between cards (wrap
 *     off); Enter/Space toggles. Tab order: one landing tab per group
 *     (focus parks on the selected card; then ArrowKeys move focus).
 *   - Visible focus ring via the existing `focus-visible:outline-*`
 *     tailwind preset used across NotificationPreferences / ChannelOrderList.
 *
 * Visual: brand-primary border + selected-highlight bg, matching the
 * hexagon-adjacent shape language set in Logo / TopNav.
 */
export interface StatePickerProps {
  mode: "single" | "multi";
  value: UsStateCode | UsStateCode[] | null;
  onChange: (next: UsStateCode | UsStateCode[] | null) => void;
  disabled?: boolean;
  /** Cap applied in multi mode only. Default 5 (the MVP catalog size). */
  maxSelections?: number;
  /** Include inactive states (greyed out, with waitlist tag). Default false. */
  showInactive?: boolean;
  /** Shortened layout for navbar / header use. */
  compact?: boolean;
  "aria-label"?: string;
}

/** Narrow a `value` prop into the multi-mode selection array. */
function asMultiValue(value: StatePickerProps["value"]): UsStateCode[] {
  if (Array.isArray(value)) return value;
  if (typeof value === "string") return [value];
  return [];
}

/** Narrow a `value` prop into the single-mode selection scalar. */
function asSingleValue(
  value: StatePickerProps["value"],
): UsStateCode | null {
  if (typeof value === "string") return value;
  if (Array.isArray(value) && value.length > 0) return value[0];
  return null;
}

export default function StatePicker({
  mode,
  value,
  onChange,
  disabled = false,
  maxSelections = 5,
  showInactive = false,
  compact = false,
  "aria-label": ariaLabel,
}: StatePickerProps) {
  const { t } = useTranslation();

  // Which states to render. `showInactive=false` is the default for the MVP.
  const states: readonly UsState[] = useMemo(
    () => (showInactive ? US_STATES : activeStates()),
    [showInactive],
  );

  // Refs let ArrowKey handlers move browser focus across cards.
  const cardRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  // Local-only flag: flips true after a blocked "add past cap" click so the
  // inline message mounts. Cleared whenever the parent-provided value
  // changes (i.e. any successful add/remove drops the user back below the cap
  // or keeps them at it — either way the message is stale by then).
  const [capHit, setCapHit] = useState(false);

  const singleSelected = mode === "single" ? asSingleValue(value) : null;
  const multiSelected = mode === "multi" ? asMultiValue(value) : [];
  const multiSet = useMemo(
    () => new Set<UsStateCode>(multiSelected),
    [multiSelected],
  );

  const atCap = mode === "multi" && multiSet.size >= maxSelections;

  function handleSelect(code: UsStateCode) {
    if (disabled) return;
    if (mode === "single") {
      const next: UsStateCode | null = singleSelected === code ? null : code;
      onChange(next);
      setCapHit(false);
      return;
    }
    // multi mode
    if (multiSet.has(code)) {
      const next = multiSelected.filter((c) => c !== code);
      onChange(next);
      setCapHit(false);
      return;
    }
    if (multiSet.size >= maxSelections) {
      // Cap hit — flash the inline message, do not mutate.
      setCapHit(true);
      return;
    }
    const next = [...multiSelected, code];
    onChange(next);
    setCapHit(false);
  }

  function focusByIndex(index: number) {
    const safe = Math.max(0, Math.min(states.length - 1, index));
    const code = states[safe]?.code;
    if (!code) return;
    cardRefs.current[code]?.focus();
  }

  function handleKeyDown(
    ev: KeyboardEvent<HTMLButtonElement>,
    index: number,
    code: UsStateCode,
  ) {
    if (disabled) return;
    switch (ev.key) {
      case "ArrowRight":
      case "ArrowDown":
        ev.preventDefault();
        focusByIndex(index + 1);
        return;
      case "ArrowLeft":
      case "ArrowUp":
        ev.preventDefault();
        focusByIndex(index - 1);
        return;
      case "Enter":
      case " ":
        ev.preventDefault();
        handleSelect(code);
        return;
      default:
        return;
    }
  }

  const groupRole = mode === "single" ? "radiogroup" : "group";
  const itemRole = mode === "single" ? "radio" : "checkbox";
  const containerClassName = compact
    ? "flex flex-wrap gap-xs"
    : "grid grid-cols-1 gap-sm sm:grid-cols-2 lg:grid-cols-3";

  return (
    <div
      data-testid="state-picker-group"
      data-compact={compact ? "true" : "false"}
      role={groupRole}
      aria-label={ariaLabel ?? t("statePicker.groupAriaLabel")}
      aria-disabled={disabled ? "true" : undefined}
      className={containerClassName}
    >
      {states.map((s, index) => {
        const selected =
          mode === "single" ? singleSelected === s.code : multiSet.has(s.code);
        const isInactive = !s.active;
        // Selected card parks tabindex=0; the rest are tabindex=-1 so Tab
        // lands on the selection exactly once per group.
        const tabIndex = selected || (index === 0 && singleSelected === null && multiSelected.length === 0) ? 0 : -1;
        const localisedName =
          t(`state.${s.code.toLowerCase()}.name`, { defaultValue: s.name });
        const cardClass = [
          "relative flex flex-col items-start gap-xs rounded-md border bg-white p-sm text-left transition-colors",
          "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary",
          selected
            ? "border-brand-primary bg-brand-primary/5"
            : "border-brand-primary/20 hover:border-brand-primary/40",
          isInactive ? "opacity-60" : "",
          disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer",
          compact ? "min-w-[96px] p-xs text-xs" : "min-w-[144px] text-sm",
        ]
          .filter(Boolean)
          .join(" ");
        return (
          <button
            key={s.code}
            ref={(el) => {
              cardRefs.current[s.code] = el;
            }}
            type="button"
            role={itemRole}
            aria-checked={selected}
            aria-disabled={disabled ? "true" : undefined}
            aria-label={t("statePicker.cardAriaLabel", {
              code: s.code,
              name: localisedName,
            })}
            data-testid={`state-picker-card-${s.code}`}
            data-state-code={s.code}
            disabled={disabled}
            tabIndex={disabled ? -1 : tabIndex}
            onClick={() => handleSelect(s.code)}
            onKeyDown={(ev) => handleKeyDown(ev, index, s.code)}
            className={cardClass}
          >
            <span className="flex w-full items-center justify-between gap-xs">
              <span
                className="inline-flex h-6 min-w-[2rem] items-center justify-center rounded-md bg-brand-primary/10 px-xs text-xs font-semibold text-brand-primary"
                aria-hidden="true"
              >
                {s.code}
              </span>
              {s.cdlAvailable && (
                <span
                  data-testid="state-picker-cdl-badge"
                  className="inline-flex h-5 items-center rounded-full bg-success/10 px-xs text-[10px] font-semibold uppercase tracking-wide text-success"
                  aria-label={t("statePicker.cdlBadgeAriaLabel")}
                >
                  {t("statePicker.cdlBadge")}
                </span>
              )}
            </span>
            <span className={compact ? "text-xs font-medium text-brand-primary" : "text-sm font-semibold text-brand-primary"}>
              {localisedName}
            </span>
            {!compact && (
              <span
                data-testid="state-picker-lang-count"
                className="text-xs text-neutral-600"
              >
                {t("statePicker.languageCount", {
                  count: s.supportedLanguages.length,
                })}
              </span>
            )}
            {isInactive && (
              <span
                data-testid="state-picker-waitlist-tag"
                className="text-[10px] font-semibold uppercase tracking-wide text-neutral-500"
              >
                {t("statePicker.waitlistTag")}
              </span>
            )}
          </button>
        );
      })}
      {mode === "multi" && atCap && capHit && (
        <p
          role="status"
          data-testid="state-picker-cap-message"
          className="col-span-full text-xs text-neutral-600"
        >
          {t("statePicker.capMessage", { max: maxSelections })}
        </p>
      )}
    </div>
  );
}
